/**
 * Services.jsx — 5 detailed service sections.
 */
import { Link } from "react-router-dom";
import { Calculator, BookOpen, Compass, Globe, Landmark, ArrowUpRight } from "lucide-react";

const services = [
    {
        num: "01",
        Icon: Calculator,
        title: "Taxation Services",
        blurb: "The core of our practice. Filing, compliance and representation across GST, income-tax and TDS.",
        items: ["GST filing & reconciliation", "TDS / TCS compliance", "Income-tax returns (Individual, Firm, Company)", "Notices, assessments & appeals"],
    },
    {
        num: "02",
        Icon: BookOpen,
        title: "Accounting Solutions",
        blurb: "Books that stand up to scrutiny. From day-to-day ledgers to full financial statements.",
        items: ["Bookkeeping (Vyapar · Tally · Cloud)", "Monthly & yearly financial statements", "Bank & statutory reconciliations", "Statutory & internal audit support"],
    },
    {
        num: "03",
        Icon: Compass,
        title: "Business Advisory",
        blurb: "For founders and boards — help with structure, compliance and long-term governance.",
        items: ["Entity registration (Pvt Ltd · LLP · Firm)", "MCA / RoC statutory compliance", "Corporate governance & board minutes", "Startup advisory & investor readiness"],
    },
    {
        num: "04",
        Icon: Globe,
        title: "NRI & International",
        blurb: "For clients whose lives cross borders. Advisory that respects both jurisdictions.",
        items: ["NRI income-tax & DTAA advisory", "Cross-border compliance (Japan · Oman · Germany)", "Repatriation & FEMA guidance", "Global asset structuring"],
    },
    {
        num: "05",
        Icon: Landmark,
        title: "Govt & Non-Govt Projects",
        blurb: "Handling institutional accounts and compliance across the north-east.",
        items: ["Government project accounts & audit", "Non-profit compliance (12A · 80G · FCRA)", "Society & trust registrations", "Grant & funds utilisation reports"],
    },
];

export default function Services() {
    return (
        <div className="font-outfit" data-testid="marketing-services">
            {/* HERO */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-16">
                <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-[#C5A059] mb-5">
                    Our practice
                </div>
                <h1 className="font-serif text-5xl md:text-6xl lg:text-[5rem] font-light tracking-tighter leading-[1.02] text-[#0A1128] max-w-5xl">
                    Expertise forged<br />over seventeen years.
                </h1>
                <p className="mt-8 text-lg lg:text-xl font-light leading-relaxed text-[#0A1128]/70 max-w-2xl">
                    From day-to-day bookkeeping to cross-border NRI advisory — five complementary
                    practices under one roof.
                </p>
            </section>

            {/* SERVICE ROWS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
                <div className="border-t border-[#0A1128]/15">
                    {services.map((s) => (
                        <ServiceRow key={s.num} {...s} />
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0A1128] text-[#FAF3E0] py-20 md:py-24">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
                    <h2 className="font-serif text-4xl lg:text-5xl font-light tracking-tight leading-tight">
                        Not sure which fits?
                    </h2>
                    <p className="mt-4 text-lg font-light text-[#FAF3E0]/70">
                        Send us a short note. We&apos;ll suggest the shortest path.
                    </p>
                    <Link
                        to="/contact"
                        data-testid="services-cta-contact"
                        className="mt-8 inline-flex items-center gap-2 bg-[#C5A059] text-[#0A1128] px-8 py-4 text-[13px] uppercase tracking-[0.2em] font-semibold hover:bg-[#FAF3E0] transition-colors duration-300"
                    >
                        Talk to us <ArrowUpRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
}

function ServiceRow({ num, Icon, title, blurb, items }) {
    return (
        <div
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-12 md:py-16 border-b border-[#0A1128]/15 group"
            data-testid={`service-row-${num}`}
        >
            <div className="lg:col-span-2">
                <div className="text-[52px] md:text-[64px] font-serif font-light text-[#C5A059] leading-none">{num}</div>
                <Icon size={30} className="text-[#0A1128] mt-6" strokeWidth={1.2} />
            </div>
            <div className="lg:col-span-5">
                <h3 className="font-serif text-3xl lg:text-4xl font-medium tracking-tight text-[#0A1128] leading-tight">
                    {title}
                </h3>
                <p className="mt-4 text-lg font-light leading-relaxed text-[#0A1128]/75 max-w-md">
                    {blurb}
                </p>
            </div>
            <div className="lg:col-span-5">
                <ul className="space-y-3">
                    {items.map((it) => (
                        <li key={it} className="flex items-start gap-3 text-[#0A1128]/85 text-base font-light leading-relaxed">
                            <span className="mt-2 w-1.5 h-1.5 bg-[#C5A059] shrink-0" />
                            {it}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
